import { Collapse, Tabs, Tag } from "antd";
import MarkdownRenderer from "@/components/UI/MarkdownRenderer";
import AddFindingActionButton from "./AddFindingActionButton";

// Renders a Role Insight report from its structured payload (the `report`
// JSON persisted with every run) as two tabs: the run's summary, and its
// findings as severity-tagged cards whose full description/evidence/
// recommendation expand on demand. The analytical content is untouched —
// only the presentation is layered.

interface FindingScope {
  level: string;
  site?: string | null;
  participant_id?: string | null;
  domain?: string | null;
}

export interface InsightFinding {
  id: string;
  kind: "issue" | "signal" | "opportunity";
  category: string;
  title: string;
  description: string;
  recommendation: string;
  evidence: string;
  severity?: "critical" | "high" | "medium" | "low" | null;
  trend_direction?: string | null;
  potential_impact?: string | null;
  scope: FindingScope;
}

export interface InsightScorecard {
  name: string;
  overall: number;
  components: Record<string, number>;
  notes?: string;
}

export interface InsightReportPayload {
  findings: InsightFinding[];
  scorecards: InsightScorecard[];
  summary: string;
}

const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

// OmniScience design-system tokens (colors_and_type.css): the clinical
// severity scale runs mild (vivo-blue) → moderate (amber) → severe
// (critical magenta); gold/green accents carry scores and positives.
const SEVERITY_STYLE: Record<string, { bg: string; text: string }> = {
  critical: { bg: "#991962", text: "#ffffff" }, // --critical-magenta
  high: { bg: "#FDE6A0", text: "#B45309" }, // --sev-moderate
  medium: { bg: "#D2EFF9", text: "#064C68" }, // --sev-mild
  low: { bg: "#E5E5E5", text: "#556C75" }, // neutral
};

const FindingTag = ({ finding }: { finding: InsightFinding }) => {
  if (finding.kind === "issue" && finding.severity) {
    const style = SEVERITY_STYLE[finding.severity] ?? SEVERITY_STYLE.low;
    return (
      <Tag
        style={{ backgroundColor: style.bg, color: style.text, border: 0 }}
        className="font-bold uppercase"
      >
        {finding.severity}
      </Tag>
    );
  }
  if (finding.kind === "signal") {
    return (
      <Tag
        style={{ backgroundColor: "#D2EFF9", color: "#064C68", border: 0 }}
        className="font-bold uppercase"
      >
        Signal{finding.trend_direction ? ` · ${finding.trend_direction}` : ""}
      </Tag>
    );
  }
  return (
    <Tag
      style={{ backgroundColor: "#CAE6D6", color: "#004A42", border: 0 }}
      className="font-bold uppercase"
    >
      Opportunity
    </Tag>
  );
};

const FindingGroup = ({
  heading,
  findings,
  roleLabel,
  trialId,
}: {
  heading: string;
  findings: InsightFinding[];
  roleLabel?: string | null;
  trialId?: string | null;
}) => {
  if (!findings.length) return null;
  return (
    <div className="mt-4">
      <p className="mb-2 text-[13px] font-bold uppercase text-[#556C75]">
        {heading}
      </p>
      <Collapse
        items={findings.map((finding) => ({
          key: finding.id,
          extra: (
            <AddFindingActionButton
              finding={finding}
              roleLabel={roleLabel}
              trialId={trialId}
            />
          ),
          label: (
            <div className="flex flex-wrap items-center gap-2">
              <FindingTag finding={finding} />
              <span className="font-semibold text-[#2B4752]">
                {finding.title}
              </span>
            </div>
          ),
          children: (
            <div className="space-y-3 text-[13px] text-[#2B4752]">
              <p>{finding.description}</p>
              {finding.potential_impact && (
                <p>
                  <span className="font-bold">Potential impact: </span>
                  {finding.potential_impact}
                </p>
              )}
              <div className="rounded-md bg-[#EFF1F2] p-3">
                <span className="font-bold">Recommendation: </span>
                {finding.recommendation}
              </div>
              <details>
                <summary className="cursor-pointer text-[12px] text-[#8AA0A9]">
                  Evidence
                </summary>
                <p className="mt-1 text-[12px] text-[#556C75]">
                  {finding.evidence}
                </p>
              </details>
            </div>
          ),
        }))}
      />
    </div>
  );
};

export default function TrialNewsReport({
  report,
  fallbackMarkdown,
  roleLabel,
  trialId,
}: {
  report: InsightReportPayload | null | undefined;
  fallbackMarkdown: string;
  /** Agent whose report this is — recorded on actions raised from a finding. */
  roleLabel?: string | null;
  trialId?: string | null;
}) {
  // Scorecards no longer count towards having something to show, so a report
  // carrying only those falls back to the markdown rather than opening on two
  // empty tabs.
  if (!report || (!report.findings?.length && !report.summary)) {
    return (
      <div className="markdown">
        <MarkdownRenderer content={fallbackMarkdown} />
      </div>
    );
  }

  const issues = report.findings
    .filter((finding) => finding.kind === "issue")
    .sort(
      (a, b) =>
        (SEVERITY_ORDER[a.severity ?? "low"] ?? 4) -
        (SEVERITY_ORDER[b.severity ?? "low"] ?? 4),
    );
  const signals = report.findings.filter(
    (finding) => finding.kind === "signal",
  );
  const opportunities = report.findings.filter(
    (finding) => finding.kind === "opportunity",
  );
  const criticalCount = issues.filter(
    (finding) => finding.severity === "critical",
  ).length;
  const highCount = issues.filter(
    (finding) => finding.severity === "high",
  ).length;

  const findingCount = issues.length + signals.length + opportunities.length;
  // The narrative only. NOT `summary_markdown` — that is the whole report from
  // InsightReport.to_markdown(): scorecard table, summary, and every finding
  // rendered again. It stays reserved for the no-structured-report fallback.
  const summaryContent = report.summary?.trim();

  // Severity counts live in the tab bar, aligned with the tab labels, so they
  // read as a heading for the Findings tab rather than as a second summary. The
  // agent's scorecards (Enrollment Health and friends) are not rendered at all:
  // composite 0-100 scores with no per-trial definition cannot be sourced when
  // asked, whereas each count here is a direct tally of this report's findings.
  const countPills = (
    <div className="flex flex-wrap items-center gap-2 text-[12px]">
      <span className="rounded-full bg-[#EBD1E0] px-2 py-0.5 font-semibold text-[#73134A]">
        {criticalCount} critical
      </span>
      <span className="rounded-full bg-[#FDE6A0] px-2 py-0.5 font-semibold text-[#B45309]">
        {highCount} high
      </span>
      <span className="rounded-full bg-[#D2EFF9] px-2 py-0.5 font-semibold text-[#064C68]">
        {signals.length} signals
      </span>
      <span className="rounded-full bg-[#CAE6D6] px-2 py-0.5 font-semibold text-[#004A42]">
        {opportunities.length} opportunities
      </span>
    </div>
  );

  return (
    <Tabs
      defaultActiveKey="summary"
      tabBarExtraContent={{ right: countPills }}
      items={[
        {
          key: "summary",
          // The narrative only — everything derived from the findings belongs on
          // the Findings tab, where it is formatted.
          label: "Summary",
          children: summaryContent ? (
            <div className="markdown text-[13px] leading-relaxed text-[#2B4752]">
              <MarkdownRenderer content={summaryContent} />
            </div>
          ) : (
            <p className="text-[13px] text-[#8AA0A9]">
              This run produced no summary.
            </p>
          ),
        },
        {
          key: "findings",
          label: `Findings${findingCount ? ` (${findingCount})` : ""}`,
          children: findingCount ? (
            <div>
              <FindingGroup
                heading="Issues"
                findings={issues}
                roleLabel={roleLabel}
                trialId={trialId}
              />
              <FindingGroup
                heading="Signals"
                findings={signals}
                roleLabel={roleLabel}
                trialId={trialId}
              />
              <FindingGroup
                heading="Opportunities"
                findings={opportunities}
                roleLabel={roleLabel}
                trialId={trialId}
              />
            </div>
          ) : (
            <p className="text-[13px] text-[#8AA0A9]">
              This run raised no issues, signals or opportunities.
            </p>
          ),
        },
      ]}
    />
  );
}
